import React from 'react';
import {
  Grid,
  Paper,
  Typography,
  Backdrop,
  CircularProgress,
  Box,
  Chip,
  Alert } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTheme } from '@mui/material/styles';
import { TFunction } from 'i18next';
import { AccountingType } from 'v2-types/order';
import { formatMoney } from 'utils/helpers';
import { SettlementPartyDetails } from './settlement-party-details';

const NET_PROCEEDS_CODES = ['netproceeds_buyer', 'netproceeds_seller'];

const useStyles = makeStyles((theme: any) => ({
  paperContainer: {
    background: theme.palette.background.paper,
    boxShadow: theme.shadows[1],
    borderRadius: theme.spacing(1),
    margin: `${theme.spacing(2.75)} ${theme.spacing(2.5)}`,
    [theme.breakpoints.down('sm')]: {
      margin: theme.spacing(1, 0),
    },
  },
  contentContainer: {
    padding: theme.spacing(4),
    gap: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
      gap: theme.spacing(1),
    },
  },
  entrySection: {
    marginBottom: theme.spacing(3),
    paddingBottom: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(2),
    },
  },
  entryHeader: {
    marginBottom: theme.spacing(2),
    paddingBottom: theme.spacing(1),
    borderBottom: `2px solid ${theme.palette.primary.main}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
      alignItems: 'baseline',
      gap: theme.spacing(1),
      marginBottom: theme.spacing(1),
    },
  },
  entryKey: {
    fontWeight: 'bold',
    color: theme.palette.primary.main,
    fontSize: '1.1rem',
    [theme.breakpoints.down('sm')]: {
      flex: 1,
      minWidth: 0,
      overflowWrap: 'anywhere',
    },
  },
  entryDescription: {
    flex: 1,
  },
  entryAmount: {
    fontWeight: 'bold',
    fontSize: '1rem',
    fontFamily: 'monospace',
    minWidth: '120px',
    textAlign: 'right',
    [theme.breakpoints.down('sm')]: {
      minWidth: 0,
      flexShrink: 0,
      whiteSpace: 'nowrap',
    },
  },
  sideColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  columnTitle: {
    fontWeight: 'bold',
    marginBottom: theme.spacing(1),
    paddingBottom: theme.spacing(0.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  payerColumn: {
    color: theme.palette.warning.main,
  },
  payeeColumn: {
    color: theme.palette.info.main,
  },
  partyItem: {
    padding: theme.spacing(1),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
  },
  partyName: {
    fontWeight: 500,
    marginBottom: theme.spacing(0.5),
  },
  partyLineContainer: {
    display: 'grid',
    gridTemplateColumns: '80px 1fr 80px',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(0.25),
    fontSize: '0.875rem',
    fontFamily: 'monospace',
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: `minmax(0, 1fr) auto ${theme.spacing(10)}`,
      alignItems: 'center',
    },
  },
  partyLineLabel: {
    textAlign: 'left',
    [theme.breakpoints.down('sm')]: {
      overflowWrap: 'anywhere',
    },
  },
  partyLineAmount: {
    textAlign: 'right',
    [theme.breakpoints.down('sm')]: {
      whiteSpace: 'nowrap',
    },
  },
  partyLinePercent: {
    textAlign: 'right',
  },
  partyAccountedInfo: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
    marginBottom: theme.spacing(0.25),
    marginTop: theme.spacing(0.5),
  },
  unaccountedAmount: {
    fontSize: '0.875rem',
    color: theme.palette.error.main,
    fontWeight: 500,
  },
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: '#fff',
  },
  differenceColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  differenceTitle: {
    fontWeight: 'bold',
    marginBottom: theme.spacing(1),
    paddingBottom: theme.spacing(0.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  differenceItem: {
    padding: theme.spacing(1),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.spacing(0.5),
  },
  differenceAmountZero: {
    color: theme.palette.success.main,
    fontWeight: 'bold',
  },
  differenceAmountNonZero: {
    color: theme.palette.error.main,
    fontWeight: 'bold',
  },
  balancedContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  balancedIcon: {
    color: theme.palette.success.main,
    fontSize: '1.5rem',
  },
  balancedText: {
    color: theme.palette.success.main,
    fontWeight: 'bold',
  },
  totalRow: {
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(2),
    borderTop: `2px solid ${theme.palette.primary.main}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  totalLabel: {
    fontWeight: 'bold',
    fontSize: '1.1rem',
  },
  totalAmount: {
    fontWeight: 'bold',
    fontSize: '1.1rem',
    fontFamily: 'monospace',
    minWidth: '120px',
    textAlign: 'right',
  },
}));

type SettlementTableProps = {
  accounting: AccountingType[];
  t: TFunction;
  isLoading: boolean;
  getPartyDetails?: (partyName: string) => { kinds: string[]; entities: string[] } | null;
};

type PendingReference = {
  id: string;
  letter: string;
  number: number;
};

const getPendingReferences = (party: any): PendingReference[] => {
  if (!party.accounted_by || !Array.isArray(party.accounted_by)) {
    return [];
  }

  const referenceMap = new Map<string, PendingReference>();

  party.accounted_by.forEach((acc: any) => {
    if (!acc.record) return;

    const { _id, letter, number } = acc.record;
    const letterTrimmed = letter ? letter.trim() : '';
    const numberValid = number != null;

    if (!letterTrimmed || !numberValid) {
      return;
    }

    // Use _id as primary key; fall back to letter-number normalized key
    const key = _id || `${letterTrimmed}-${number}`;

    if (!referenceMap.has(key)) {
      referenceMap.set(key, {
        id: key,
        letter: letterTrimmed,
        number,
      });
    }
  });

  return Array.from(referenceMap.values());
};

export const SettlementTable: React.FC<SettlementTableProps> = ({
  accounting,
  t,
  isLoading,
  getPartyDetails,
}) => {
  const classes = useStyles();
  const theme = useTheme();

  if (accounting.length === 0) {
    return (
      <Paper className={classes.paperContainer}>
        <Grid container className={classes.contentContainer} direction="column">
          <Typography variant="body1" color="textSecondary">
            {t('settlement:no-data')}
          </Typography>
        </Grid>
      </Paper>
    );
  }

  return (
    <Paper className={classes.paperContainer}>
      <Grid container className={classes.contentContainer} direction="column">
        {accounting.map((entry) => {
          const amount = entry.amount_override !== 0 ? entry.amount_override : entry.amount_calculated;
          const entryKey = (typeof entry.letter === 'string' && entry.letter.trim() !== '' && entry.number != null) ? `${entry.letter.trim()}-${entry.number}` : 'N/A';

          const payers = (entry.involved_parties || []).filter(
            (p: any) => p.kind === 'Payer' && (p.amount || 0) > 0
          );

          const payees = (entry.involved_parties || []).filter(
            (p: any) => p.kind === 'Payee' && (p.amount || 0) > 0
          );

          const isNetProceeds = NET_PROCEEDS_CODES.includes(entry.code);

          if (payers.length === 0 && payees.length === 0 && !isNetProceeds) {
            return null;
          }

          return (
            <Box key={entry._id} className={classes.entrySection}>
              <Box className={classes.entryHeader}>
                <Typography className={classes.entryKey}>
                  {entryKey}
                  {' '}
                  -
                  {entry.description}
                </Typography>
                <Typography className={classes.entryAmount}>
                  {formatMoney(amount, 2)}
                </Typography>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} lg={4}>
                  <Box className={classes.sideColumn}>
                    <Typography className={`${classes.columnTitle} ${classes.payerColumn}`}>
                      {t('settlement:payers', 'Payers')}
                    </Typography>
                    {payers.length === 0 ? (
                      <Typography variant="body2" color="textSecondary">
                        {t('settlement:no-payers', 'No payers')}
                      </Typography>
                    ) : (
                      payers.map((payer: any) => {
                        const partyAmount = (payer.percent || 0) * amount;
                        const accountedAmount = payer.accounted_amount || 0;
                        const accountedPercent = partyAmount > 0 ? ((accountedAmount / partyAmount) * 100).toFixed(1) : '0.0';
                        const unaccountedAmount = partyAmount - accountedAmount;
                        const unaccountedPercent = partyAmount > 0 ? ((unaccountedAmount / partyAmount) * 100).toFixed(1) : '0.0';

                        const accountedEntries = (payer.accounted_by || [])
                          .map((acc: any) => acc.record?.description)
                          .filter(Boolean);

                        const pendingRefs = !entry.posted ? getPendingReferences(payer) : [];

                        return (
                          <Box key={payer._id} className={classes.partyItem}>
                            <Typography className={classes.partyName}>
                              {payer.name}
                            </Typography>
                            {getPartyDetails && (
                              <SettlementPartyDetails
                                isOverride={entry.amount_override !== 0}
                                calculatedAmount={entry.amount_calculated}
                                overriddenAmount={entry.amount_override}
                                entryDate={entry.entry_date}
                                isPosted={entry.posted}
                                partyRoles={getPartyDetails(payer.name)?.kinds || []}
                                entities={getPartyDetails(payer.name)?.entities || []}
                                t={t}
                              />
                            )}
                            {pendingRefs.length > 0 && (
                              <>
                                <Typography variant="caption" style={{ display: 'block', marginBottom: theme.spacing(0.5), color: theme.palette.warning.main, fontWeight: 500 }}>
                                  {t('settlement:pending-references', 'Pending References')}
                                </Typography>
                                <Box style={{ display: 'flex', gap: theme.spacing(0.5), flexWrap: 'wrap', marginBottom: theme.spacing(1) }}>
                                  {pendingRefs.map((ref) => (
                                    <Chip
                                      key={ref.id}
                                      label={`${ref.letter}-${ref.number}`}
                                      size="small"
                                      style={{ backgroundColor: theme.palette.warning.light, color: '#fff' }}
                                    />
                                  ))}
                                </Box>
                              </>
                            )}
                            <Box className={classes.partyLineContainer}>
                              <Box className={classes.partyLineLabel} />
                              <Box className={classes.partyLineAmount}>
                                {formatMoney(partyAmount, 2)}
                              </Box>
                              <Box className={classes.partyLinePercent}>
                                <Chip label={`${((payer.percent || 0) * 100).toFixed(1)}%`} size="small" />
                              </Box>
                            </Box>
                            <Box className={classes.partyLineContainer}>
                              <Box className={classes.partyLineLabel}>
                                {t('settlement:accounted', 'Accounted')}
                                :
                              </Box>
                              <Box className={classes.partyLineAmount}>
                                {formatMoney(accountedAmount, 2)}
                              </Box>
                              <Box className={classes.partyLinePercent}>
                                <Chip label={`${accountedPercent}%`} size="small" />
                              </Box>
                            </Box>
                            {unaccountedAmount > 0.01 && (
                              <Box className={classes.partyLineContainer}>
                                <Box className={classes.partyLineLabel} style={{ color: theme.palette.error.main, fontWeight: 500 }}>
                                  {t('settlement:rest', 'Rest')}
                                  :
                                </Box>
                                <Box className={classes.partyLineAmount} style={{ color: theme.palette.error.main, fontWeight: 500 }}>
                                  {formatMoney(unaccountedAmount, 2)}
                                </Box>
                                <Box className={classes.partyLinePercent} style={{ color: theme.palette.error.main, fontWeight: 500 }}>
                                  <Chip label={`${unaccountedPercent}%`} size="small" style={{ backgroundColor: theme.palette.error.main, color: 'white' }} />
                                </Box>
                              </Box>
                            )}
                            {accountedEntries.length > 0 && (
                              <Typography className={classes.partyAccountedInfo}>
                                {accountedEntries.join(', ')}
                              </Typography>
                            )}
                          </Box>
                        );
                      })
                    )}
                  </Box>
                </Grid>

                <Grid item xs={12} lg={4}>
                  <Box className={classes.sideColumn}>
                    <Typography className={`${classes.columnTitle} ${classes.payeeColumn}`}>
                      {t('settlement:payees', 'Payees')}
                    </Typography>
                    {payees.length === 0 ? (
                      <Typography variant="body2" color="textSecondary">
                        {t('settlement:no-payees', 'No payees')}
                      </Typography>
                    ) : (
                      payees.map((payee: any) => {
                        const partyAmount = (payee.percent || 0) * amount;
                        const accountedAmount = payee.accounted_amount || 0;
                        const accountedPercent = partyAmount > 0 ? ((accountedAmount / partyAmount) * 100).toFixed(1) : '0.0';
                        const unaccountedAmount = partyAmount - accountedAmount;
                        const unaccountedPercent = partyAmount > 0 ? ((unaccountedAmount / partyAmount) * 100).toFixed(1) : '0.0';

                        const accountedEntries = (payee.accounted_by || [])
                          .map((acc: any) => acc.record?.description)
                          .filter(Boolean);

                        const pendingRefs = !entry.posted ? getPendingReferences(payee) : [];

                        return (
                          <Box key={payee._id} className={classes.partyItem}>
                            <Typography className={classes.partyName}>
                              {payee.name}
                            </Typography>
                            {getPartyDetails && (
                              <SettlementPartyDetails
                                isOverride={entry.amount_override !== 0}
                                calculatedAmount={entry.amount_calculated}
                                overriddenAmount={entry.amount_override}
                                entryDate={entry.entry_date}
                                isPosted={entry.posted}
                                partyRoles={getPartyDetails(payee.name)?.kinds || []}
                                entities={getPartyDetails(payee.name)?.entities || []}
                                t={t}
                              />
                            )}
                            {pendingRefs.length > 0 && (
                              <>
                                <Typography variant="caption" style={{ display: 'block', marginBottom: theme.spacing(0.5), color: theme.palette.warning.main, fontWeight: 500 }}>
                                  {t('settlement:pending-references', 'Pending References')}
                                </Typography>
                                <Box style={{ display: 'flex', gap: theme.spacing(0.5), flexWrap: 'wrap', marginBottom: theme.spacing(1) }}>
                                  {pendingRefs.map((ref) => (
                                    <Chip
                                      key={ref.id}
                                      label={`${ref.letter}-${ref.number}`}
                                      size="small"
                                      style={{ backgroundColor: theme.palette.warning.light, color: '#fff' }}
                                    />
                                  ))}
                                </Box>
                              </>
                            )}
                            <Box className={classes.partyLineContainer}>
                              <Box className={classes.partyLineLabel} />
                              <Box className={classes.partyLineAmount}>
                                {formatMoney(partyAmount, 2)}
                              </Box>
                              <Box className={classes.partyLinePercent}>
                                <Chip label={`${((payee.percent || 0) * 100).toFixed(1)}%`} size="small" />
                              </Box>
                            </Box>
                            <Box className={classes.partyLineContainer}>
                              <Box className={classes.partyLineLabel}>
                                {t('settlement:accounted', 'Accounted')}
                                :
                              </Box>
                              <Box className={classes.partyLineAmount}>
                                {formatMoney(accountedAmount, 2)}
                              </Box>
                              <Box className={classes.partyLinePercent}>
                                <Chip label={`${accountedPercent}%`} size="small" />
                              </Box>
                            </Box>
                            {unaccountedAmount > 0.01 && (
                              <Box className={classes.partyLineContainer}>
                                <Box className={classes.partyLineLabel} style={{ color: theme.palette.error.main, fontWeight: 500 }}>
                                  {t('settlement:rest', 'Rest')}
                                  :
                                </Box>
                                <Box className={classes.partyLineAmount} style={{ color: theme.palette.error.main, fontWeight: 500 }}>
                                  {formatMoney(unaccountedAmount, 2)}
                                </Box>
                                <Box className={classes.partyLinePercent} style={{ color: theme.palette.error.main, fontWeight: 500 }}>
                                  <Chip label={`${unaccountedPercent}%`} size="small" style={{ backgroundColor: theme.palette.error.main, color: 'white' }} />
                                </Box>
                              </Box>
                            )}
                            {accountedEntries.length > 0 && (
                              <Typography className={classes.partyAccountedInfo}>
                                {accountedEntries.join(', ')}
                              </Typography>
                            )}
                          </Box>
                        );
                      })
                    )}
                  </Box>
                </Grid>

                <Grid item xs={12} lg={4}>
                  <Box className={classes.differenceColumn}>
                    <Typography className={classes.differenceTitle}>
                      {t('settlement:difference', 'Difference')}
                    </Typography>
                    {(() => {
                      const payersAccounted = payers.reduce((sum, p) => sum + (p.accounted_amount || 0), 0);
                      const payeesAccounted = payees.reduce((sum, p) => sum + (p.accounted_amount || 0), 0);
                      const effectiveAmount = amount;

                      const hasPayersAccounted = payersAccounted > 0.01;
                      const hasPayeesAccounted = payeesAccounted > 0.01;
                      const canCompareDifference = hasPayersAccounted || hasPayeesAccounted;

                      const difference = Math.abs(payersAccounted - payeesAccounted);
                      const hasDifference = difference > 0.01;
                      const isBalanced = (
                        payers.length > 0
                        && payees.length > 0
                        && Math.abs(payersAccounted - payeesAccounted) <= 0.01
                        && Math.abs(payersAccounted - effectiveAmount) <= 0.01
                        && Math.abs(payeesAccounted - effectiveAmount) <= 0.01
                      );

                      const renderDifferenceStatus = () => {
                        if (!canCompareDifference) {
                          return (
                            <Alert severity="warning" style={{ padding: '8px' }}>
                              <Typography variant="caption">
                                {t('settlement:incomplete-data-warning', 'Need at least one accounted payer or one accounted payee to compare')}
                              </Typography>
                            </Alert>
                          );
                        }

                        if (isBalanced) {
                          return (
                            <Box className={classes.balancedContainer}>
                              <FontAwesomeIcon icon="check-circle" className={classes.balancedIcon} />
                              <Typography className={classes.balancedText}>
                                {t('settlement:balanced', 'Balanced')}
                              </Typography>
                            </Box>
                          );
                        }

                        if (hasDifference) {
                          return (
                            <>
                              <Typography className={classes.differenceAmountNonZero}>
                                {formatMoney(difference, 2)}
                              </Typography>
                              <Alert severity="error" style={{ marginTop: '8px', padding: '8px' }}>
                                <Typography variant="caption">
                                  {t('settlement:imbalance-warning', 'Payers and payees are not balanced')}
                                </Typography>
                              </Alert>
                            </>
                          );
                        }

                        return (
                          <>
                            <Typography className={classes.differenceAmountNonZero}>
                              {formatMoney(Math.abs(effectiveAmount - payersAccounted), 2)}
                            </Typography>
                            <Alert severity="error" style={{ marginTop: '8px', padding: '8px' }}>
                              <Typography variant="caption">
                                {t('settlement:imbalance-warning', 'Payers and payees are not fully accounted')}
                              </Typography>
                            </Alert>
                          </>
                        );
                      };

                      return (
                        <Box className={classes.differenceItem}>
                          {renderDifferenceStatus()}
                        </Box>
                      );
                    })()}
                  </Box>
                </Grid>
              </Grid>
            </Box>
          );
        })}
        <Box className={classes.totalRow}>
          <Typography className={classes.totalLabel}>
            {t('settlement:total', 'Total')}
          </Typography>
          <Typography className={classes.totalAmount}>
            {formatMoney(accounting.reduce((sum, entry) => {
              const effectiveAmount =
                entry.amount_override ||
                entry.amount_calculated ||
                entry.amount;
              return sum + (effectiveAmount > 0 ? effectiveAmount : 0);
            }, 0), 2)}
          </Typography>
        </Box>
      </Grid>
      <Backdrop open={isLoading} className={classes.backdrop}>
        <CircularProgress color="primary" />
      </Backdrop>
    </Paper>
  );
};
